(function () {
  const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];

  function clamp(number, min, max) {
    return Math.max(min, Math.min(max, number));
  }

  function confidenceFromDistance(distance) {
    const cleanDistance = clamp(Number(distance) || 0, 0, 0.5);
    const confidence = Math.round(clamp(94 - cleanDistance * 18, 84, 96));
    const secondConfidence = Math.round(clamp(76 + cleanDistance * 14, 68, confidence - 7));

    return { confidence, secondConfidence };
  }

  function confidenceFromRangeScore(score) {
    const cleanScore = Math.max(0, Number(score) || 0);
    const confidence = Math.round(clamp(94 - cleanScore * 10, 78, 95));
    const gap = Math.round(clamp(10 + cleanScore * 4, 10, 24));
    const secondConfidence = Math.round(clamp(confidence - gap, 62, confidence - 7));

    return { confidence, secondConfidence };
  }

  function normalizeSize(value) {
    return String(value || "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace("2XL", "XXL")
      .replace("XXXL", "3XL")
      .replace("3XL+", "3XL");
  }

  function variantSize(variant) {
    const values = [];

    if (variant.title) values.push(variant.title);
    if (Array.isArray(variant.options)) values.push(...variant.options);

    const joined = values.map(normalizeSize).join("|");

    if (/(^|\|)3XL(\+)?($|\|)/.test(joined)) return "3XL";
    if (/(^|\|)XXL($|\|)/.test(joined)) return "XXL";
    if (/(^|\|)XL($|\|)/.test(joined)) return "XL";
    if (/(^|\|)L($|\|)/.test(joined)) return "L";
    if (/(^|\|)M($|\|)/.test(joined)) return "M";
    if (/(^|\|)S($|\|)/.test(joined)) return "S";
    if (/(^|\|)XS($|\|)/.test(joined)) return "XS";

    return "";
  }

  const fitProfiles = {
    shirt: {
      label: "חולצת כדורגל",
      note: "ענה על כמה שאלות קצרות ונחשב לך המלצת מידה לחולצת כדורגל.",
      subtitle: "החישוב מבוסס על גובה, משקל והעדפת גזרה. ההמלצה מיועדת לחולצות כדורגל בלבד.",
      resultCopy: "בחולצת כדורגל"
    },
    jacket: {
      label: "ג׳קט",
      note: "ענה על כמה שאלות קצרות ונחשב לך המלצת מידה לג׳קט.",
      subtitle: "החישוב מבוסס על גובה, משקל והעדפת גזרה, לפי טבלת המידות של הג׳קטים.",
      resultCopy: "בג׳קט"
    }
  };

  const jacketRanges = [
    { size: "S", height: [165, 170], weight: [45, 60] },
    { size: "M", height: [170, 175], weight: [60, 68] },
    { size: "L", height: [170, 175], weight: [68, 75] },
    { size: "XL", height: [175, 180], weight: [75, 82] },
    { size: "XXL", height: [180, 185], weight: [82, 95] }
  ];

  function distanceToRange(value, range) {
    if (value >= range[0] && value <= range[1]) return 0;
    if (value < range[0]) return range[0] - value;
    return value - range[1];
  }

  function estimateByRanges(height, weight, age, fitPreference, ranges) {
    let best = ranges[0];
    let bestScore = Infinity;

    ranges.forEach((range) => {
      const heightScore = distanceToRange(height, range.height) / 4;
      const weightScore = distanceToRange(weight, range.weight) / 6;
      const middleWeight = (range.weight[0] + range.weight[1]) / 2;
      const tieBreaker = Math.abs(weight - middleWeight) / 60;
      const score = heightScore + weightScore + tieBreaker;

      if (score < bestScore) {
        bestScore = score;
        best = range;
      }
    });

    let index = sizeOrder.indexOf(best.size);

    if (fitPreference === "tight") index -= 1;
    if (fitPreference === "loose") index += 1;
    if (age >= 55 && fitPreference !== "tight") index += 0;

    index = clamp(index, 0, sizeOrder.length - 1);

    let secondIndex = fitPreference === "tight" ? index + 1 : index - 1;
    if (secondIndex < 0 || secondIndex >= sizeOrder.length || secondIndex === index) {
      secondIndex = clamp(index + 1, 0, sizeOrder.length - 1);
    }

    const confidencePair = confidenceFromRangeScore(bestScore);

    return {
      size: sizeOrder[index],
      confidence: confidencePair.confidence,
      secondSize: sizeOrder[secondIndex],
      secondConfidence: confidencePair.secondConfidence
    };
  }

  function estimateSize(height, weight, age, fitPreference, fitType) {
    if (fitType === "jacket") {
      return estimateByRanges(height, weight, age, fitPreference, jacketRanges);
    }

    let score = (weight - 52) / 11 + (height - 168) / 14;

    if (age >= 45) score += 0.12;
    if (age >= 60) score += 0.12;

    if (fitPreference === "tight") score -= 0.42;
    if (fitPreference === "loose") score += 0.48;

    const index = clamp(Math.round(score), 0, sizeOrder.length - 1);
    const exact = clamp(score, 0, sizeOrder.length - 1);
    const distance = Math.abs(exact - index);
    const confidencePair = confidenceFromDistance(distance);

    let secondIndex = exact > index ? index + 1 : index - 1;
    secondIndex = clamp(secondIndex, 0, sizeOrder.length - 1);

    if (secondIndex === index) {
      secondIndex = clamp(index + 1, 0, sizeOrder.length - 1);
    }

    return {
      size: sizeOrder[index],
      confidence: confidencePair.confidence,
      secondSize: sizeOrder[secondIndex],
      secondConfidence: confidencePair.secondConfidence
    };
  }

  function nearestAvailableSize(size, productData) {
    const availableSizes = Array.from(new Set((productData.variants || [])
      .filter((variant) => variant.available)
      .map(variantSize)
      .filter(Boolean)));

    if (!availableSizes.length) {
      return { size, exact: false, variant: null };
    }

    if (availableSizes.includes(size)) {
      return {
        size,
        exact: true,
        variant: findVariantBySize(size, productData)
      };
    }

    const wantedIndex = sizeOrder.indexOf(size);
    let best = availableSizes[0];
    let bestDistance = 99;

    availableSizes.forEach((availableSize) => {
      const distance = Math.abs(sizeOrder.indexOf(availableSize) - wantedIndex);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = availableSize;
      }
    });

    return {
      size: best,
      exact: false,
      variant: findVariantBySize(best, productData)
    };
  }

  function findVariantBySize(size, productData) {
    return (productData.variants || []).find((variant) => {
      return variant.available && variantSize(variant) === size;
    }) || null;
  }

  function setStep(root, stepNumber) {
    root.querySelectorAll("[data-regista-fit-step]").forEach((step) => {
      step.classList.toggle("is-active", step.getAttribute("data-regista-fit-step") === String(stepNumber));
    });
  }

  function getProductData(root) {
    const script = root.querySelector("[data-regista-fit-product]");
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function renderBars(root, result) {
    const bars = root.querySelector("[data-regista-fit-bars]");
    if (!bars) return;

    bars.innerHTML = `
      <div class="regista-fit-bar">
        <div class="regista-fit-bar__head"><span>${result.size}</span><span>${result.confidence}%</span></div>
        <div class="regista-fit-bar__track"><div class="regista-fit-bar__fill" style="width:${result.confidence}%"></div></div>
      </div>
      <div class="regista-fit-bar regista-fit-bar--secondary">
        <div class="regista-fit-bar__head"><span>${result.secondSize}</span><span>${result.secondConfidence}%</span></div>
        <div class="regista-fit-bar__track"><div class="regista-fit-bar__fill" style="width:${result.secondConfidence}%"></div></div>
      </div>
    `;
  }

  function selectVariant(productData, variantId) {
    if (!productData || !variantId) return false;

    const form = document.getElementById(productData.formId);
    const picker = document.querySelector(`variant-picker[form="${CSS.escape(productData.formId)}"]`);

    if (picker && typeof picker.selectVariant === "function") {
      picker.selectVariant(Number(variantId));
      return true;
    }

    const masterInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
    if (masterInput) {
      masterInput.disabled = false;
      masterInput.value = String(variantId);
      masterInput.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    return false;
  }

  function initModal(modal) {
    if (modal.dataset.registaFitReady === "true") return;
    modal.dataset.registaFitReady = "true";

    const productData = getProductData(modal);
    const fitType = productData && productData.fitType ? productData.fitType : "shirt";
    const profile = fitProfiles[fitType] || fitProfiles.shirt;

    const state = {
      height: 0,
      weight: 0,
      age: 0,
      fit: "regular",
      finalSize: "",
      finalVariantId: null
    };

    const heightInput = modal.querySelector("[data-regista-fit-height]");
    const weightInput = modal.querySelector("[data-regista-fit-weight]");
    const ageInput = modal.querySelector("[data-regista-fit-age]");
    const note = modal.querySelector("[data-regista-fit-note]");
    const subtitle = modal.querySelector("[data-regista-fit-subtitle]");
    const fitQuestion = modal.querySelector("[data-regista-fit-question]");
    const fitGuide = modal.querySelector("[data-regista-fit-guide]");

    if (note) note.textContent = profile.note;
    if (subtitle) subtitle.textContent = profile.subtitle;
    if (fitQuestion) fitQuestion.textContent = `איך אתה אוהב שה${profile.label} יושב?`;
    if (fitGuide) fitGuide.textContent = fitType === "jacket" ? "בג׳קטים, אם אתה מתכנן ללבוש שכבה מתחת או אוהב יותר מרווח, בדרך כלל עדיף לבחור גזרה רחבה יותר." : "בחולצות כדורגל, מי שאוהב יותר מרווח בדרך כלל יעדיף לעלות מידה.";

    function validateStepOne() {
      const height = Number(heightInput.value);
      const weight = Number(weightInput.value);
      const button = modal.querySelector('[data-regista-fit-step="1"] [data-regista-fit-next]');
      button.disabled = !(height >= 120 && height <= 230 && weight >= 35 && weight <= 180);
    }

    function validateStepTwo() {
      const age = Number(ageInput.value);
      const button = modal.querySelector('[data-regista-fit-step="2"] [data-regista-fit-next]');
      button.disabled = !(age >= 12 && age <= 100);
    }

    heightInput.addEventListener("input", validateStepOne);
    weightInput.addEventListener("input", validateStepOne);
    ageInput.addEventListener("input", validateStepTwo);

    modal.querySelectorAll("[data-regista-fit-next]").forEach((button) => {
      button.addEventListener("click", () => {
        const currentStep = button.closest("[data-regista-fit-step]").getAttribute("data-regista-fit-step");

        if (currentStep === "1") {
          state.height = Number(heightInput.value);
          state.weight = Number(weightInput.value);
          if (note) note.textContent = "כבר יש כיוון ראשוני. עוד שתי שאלות קצרות כדי לדייק את ההמלצה.";
          setStep(modal, 2);
        } else if (currentStep === "2") {
          state.age = Number(ageInput.value);
          if (note) note.textContent = "כמעט סיימנו. עכשיו נשאר לבחור איך אתה אוהב שהחולצה יושבת.";
          setStep(modal, 3);
        }
      });
    });

    modal.querySelectorAll("[data-regista-fit-back]").forEach((button) => {
      button.addEventListener("click", () => {
        const currentStep = Number(button.closest("[data-regista-fit-step]").getAttribute("data-regista-fit-step"));
        setStep(modal, Math.max(1, currentStep - 1));
      });
    });

    modal.querySelectorAll("[data-fit-pref]").forEach((button) => {
      button.addEventListener("click", () => {
        modal.querySelectorAll("[data-fit-pref]").forEach((item) => item.classList.remove("is-selected"));
        button.classList.add("is-selected");
        state.fit = button.getAttribute("data-fit-pref") || "regular";
      });
    });

    modal.querySelector("[data-regista-fit-calculate]").addEventListener("click", () => {
      const estimate = estimateSize(state.height, state.weight, state.age, state.fit, fitType);
      const available = nearestAvailableSize(estimate.size, productData || { variants: [] });
      const shownSize = available.size || estimate.size;
      const exact = available.exact;

      state.finalSize = shownSize;
      state.finalVariantId = available.variant ? available.variant.id : null;

      const title = modal.querySelector("[data-regista-fit-result-title]");
      const size = modal.querySelector("[data-regista-fit-size]");
      const copy = modal.querySelector("[data-regista-fit-result-copy]");
      const selectButton = modal.querySelector("[data-regista-fit-select-size]");

      title.textContent = exact ? "המידה המומלצת עבורך קיימת במלאי" : "זו המידה הקרובה ביותר שקיימת במלאי";
      size.textContent = shownSize;

      const finalResult = {
        size: shownSize,
        confidence: exact ? estimate.confidence : Math.max(76, estimate.confidence - 6),
        secondSize: estimate.secondSize === shownSize ? estimate.size : estimate.secondSize,
        secondConfidence: estimate.secondConfidence
      };

      renderBars(modal, finalResult);

      if (exact) {
        copy.innerHTML = `לפי הגובה, המשקל והעדפת הגזרה שבחרת, יש סיכוי גבוה שמידה <strong>${shownSize}</strong> תהיה הבחירה הנוחה עבורך ${profile.resultCopy}.`;
      } else {
        copy.innerHTML = `לפי הנתונים שלך ההמלצה הראשונית היא <strong>${estimate.size}</strong>, אבל היא לא זמינה כרגע במוצר הזה. המידה הקרובה ביותר במלאי היא <strong>${shownSize}</strong>.`;
      }

      selectButton.textContent = state.finalVariantId ? `בחר מידה ${shownSize}` : "המידה לא זמינה כרגע";
      selectButton.disabled = !state.finalVariantId;

      if (note) note.textContent = "ההמלצה נשארת אצלך בדפדפן בלבד. אנחנו לא שומרים את הנתונים האלה.";
      setStep(modal, 4);
    });

    modal.querySelector("[data-regista-fit-select-size]").addEventListener("click", () => {
      if (selectVariant(productData, state.finalVariantId)) {
        closeModal(modal);
      }
    });

    modal.querySelector("[data-regista-fit-restart]").addEventListener("click", () => {
      if (note) note.textContent = profile.note;
      setStep(modal, 1);
    });
  }

  function openModal(modal) {
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    initModal(modal);
    if (!modal.classList.contains("is-open")) {
      modal.__registaReturnFocus = document.activeElement;
      modal.__registaBodyOverflow = document.body.style.overflow;
      modal.__registaHtmlOverflow = document.documentElement.style.overflow;
    }
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("regista-fit-modal-open");
    document.body.classList.add("regista-fit-modal-open");
    document.body.style.overflow = "hidden";
    setStep(modal, 1);
    window.setTimeout(() => {
      const first = modal.querySelector("[data-regista-fit-close], button, input, select, [tabindex]:not([tabindex='-1'])");
      if (first && typeof first.focus === "function") first.focus();
    }, 20);
  }

  function closeModal(modal) {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("regista-fit-modal-open");
    document.body.classList.remove("regista-fit-modal-open");
    document.body.style.overflow = modal.__registaBodyOverflow || "";
    document.documentElement.style.overflow = modal.__registaHtmlOverflow || "";
    const returnFocus = modal.__registaReturnFocus;
    modal.__registaReturnFocus = null;
    if (returnFocus && typeof returnFocus.focus === "function") returnFocus.focus();
  }

  document.addEventListener("click", (event) => {
    const openButton = event.target.closest("[data-regista-fit-open]");
    if (openButton) {
      const container = openButton.closest(".product-info, .quick-buy, body");
      const modal = (container && container.querySelector("[data-regista-fit-modal]")) || document.querySelector("[data-regista-fit-modal]");
      if (modal) openModal(modal);
      return;
    }

    const closeButton = event.target.closest("[data-regista-fit-close]");
    if (closeButton) {
      const modal = closeButton.closest("[data-regista-fit-modal]");
      if (modal) closeModal(modal);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll("[data-regista-fit-modal].is-open").forEach(closeModal);
  });
})();
